import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { parseCsvRows, parseRateSheet, type ParsedRateSheet } from '@arcnet/contracts';

export interface SheetRead {
  id: string; how: 'direct' | 'ai' | null; notes: string[]; currency: string | null;
  seconds: number; status: string; error: string | null;
}

/**
 * Reading a carrier's rate sheet for a preview, whatever its shape: a CSV the browser can read is
 * read here; anything else -- a PDF, a spreadsheet, a CSV whose columns are not recognisable -- goes
 * to the API, which reads a spreadsheet directly and a PDF with AI (carriers/ratesheetai.ts), and
 * comes back as a CSV. Either way the browser parses it with the same parser the API validates
 * against, so the preview is what is stored. Used for a client's own trunk (OwnRateSheet) and the
 * platform's carrier sheets (billing/RateSheetTools).
 *
 * `readBase` is the API path the file is posted to, with `/:jobId` polled beside it.
 */
export function useRateSheetRead(readBase: string, key: string) {
  const [file, setFile] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedRateSheet | { error: string } | null>(null);
  const [ai, setAi] = useState<SheetRead | null>(null);
  const [blob, setBlob] = useState<File | null>(null);

  const send = async (f: File, forceAi = false) => {
    setParsed(null);
    const data = await new Promise<string>((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(String(r.result).replace(/^data:[^,]*,/, ''));
      r.onerror = () => rej(new Error('The file could not be read.'));
      r.readAsDataURL(f);
    });
    try {
      const { id } = await api.post<{ id: string }>(readBase, { file_name: f.name, data, ...(forceAi ? { force_ai: true } : {}) });
      setAi({ id, how: null, notes: [], currency: null, seconds: 0, status: 'reading', error: null });
    } catch (e) {
      setParsed({ error: e instanceof Error ? e.message : 'The file could not be sent.' });
    }
  };
  const job = useQuery({
    queryKey: ['rate-sheet-read', key, ai?.id],
    queryFn: () => api.get<{ status: 'reading' | 'done' | 'failed'; how: 'direct' | 'ai' | null; csv: string | null; currency: string | null; notes: string[]; error: string | null; seconds: number }>(`${readBase}/${ai!.id}`),
    enabled: !!ai && ai.status === 'reading',
    refetchInterval: 2_000,
    refetchIntervalInBackground: true,
  });
  useEffect(() => {
    const j = job.data;
    if (!j || !ai || j.status === 'reading') { if (j && ai) setAi({ ...ai, seconds: j.seconds }); return; }
    setAi({ ...ai, status: j.status, how: j.how, notes: j.notes, currency: j.currency, error: j.error, seconds: j.seconds });
    if (j.status === 'done' && j.csv) setParsed(parseRateSheet(parseCsvRows(j.csv), '27'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.data]);
  useEffect(() => { if (job.error && ai) setAi({ ...ai, status: 'failed', error: 'The read was lost (the server restarted). Try again.' }); }, [job.error]); // eslint-disable-line react-hooks/exhaustive-deps

  const read = async (f: File) => {
    setFile(f.name); setBlob(f); setAi(null);
    if (/\.(csv|txt)$/i.test(f.name)) {
      const local = parseRateSheet(parseCsvRows(await f.text()), '27');
      if (!('error' in local) && local.rules.length > 0) { setParsed(local); return; }
    }
    await send(f);
  };
  const reset = () => { setParsed(null); setFile(null); setAi(null); setBlob(null); };
  const ok = parsed && !('error' in parsed) ? parsed : null;
  return { file, parsed, setParsed, ai, blob, read, send, reset, ok };
}
